import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SabrilPixarAvatar } from './SabrilPixarAvatar';

/**
 * ResultsStorytellingBlock — 4-pillar storytelling section that builds awareness
 * before the offer reveal. Each pillar = a real anecdote + a hard truth + a CTA-style "what it costs you".
 *
 * Pillars:
 *  1. ADN — a resto without identity dies
 *  2. Pilotage industriel — a resto without industrial piloting can't scale
 *  3. Présence sociale unique — a resto without a unique social strategy can't grow CA
 *  4. Environnement 360° — nothing left to chance
 *
 * Props:
 *  - restoName: optional, for personalized punch
 *  - firstName: optional
 */

const PILLARS = [
  {
    id: 'adn',
    icon: '🧬',
    accent: '#A78BFA', // violet — identity
    title: 'UN RESTO SANS ADN',
    subtitle: 'ne peut pas fonctionner.',
    quote: "« Quand t'as 50 plats et aucune histoire, t'as pas un concept. T'as un buffet. »",
    anecdote: "Philippe, à Marrakech. Il rachète un établissement qui tournait depuis 7 ans avec une carte de 42 plats, une identité absente, une histoire jamais racontée. On a tout restructuré de A à Z : nouveau positionnement (cuisine signature contemporaine), nom revu, identité visuelle complète, histoire de marque qui parle, carte resserrée à 18 plats avec 3 signatures fortes. Résultat 8 mois après réouverture : CA x2 vs ancien établissement, ticket moyen +94%, et une attente moyenne de 3 semaines pour réserver le week-end.",
    lesson: 'Sans ADN clair, votre resto est interchangeable. Vos clients ne te recommandent pas. Vous attires des promo-hunters, pas des fans.',
    cost: 'Chaque mois sans ADN, c\'est 800-1500€ de marge brûlée en plats qui ne se vendent pas + clients perdus avant même qu\'ils passent la porte.',
  },
  {
    id: 'pilotage',
    icon: '🎛️',
    accent: '#F05A1A', // orange — operations
    title: 'UN RESTO SANS PILOTAGE INDUSTRIEL',
    subtitle: 'ne fera pas d\'argent et ne se duplique pas.',
    quote: "« Si t'es pas là un week-end et que ça part en couilles, t'as pas un resto — t'as un job. »",
    anecdote: "Karim, deux établissements à Dubai (DIFC + Marina). 80h/semaine entre les deux sites. Quand il a voulu ouvrir le 3ème à Riyadh, blocage total — tout reposait sur lui et son associé. Process à l'oral, planning sur un groupe WhatsApp, stocks à la louche. On a codifié 31 process en 90 jours, mis en place un dashboard temps réel, formé deux General Managers autonomes. Aujourd'hui 4 sites en GCC, Karim bosse 38h/semaine, son frère gère les nouvelles ouvertures.",
    lesson: 'Sans pilotage industriel (process écrits, ratios suivis live, équipe autonome), t\'as un boulot précaire — pas un business. Vous ne peux ni vendre, ni dupliquer, ni partir 1 semaine.',
    cost: 'Sans pilotage, t\'es coincé à 1 site pour toute votre vie. La valeur de revente de votre resto = max 1 an de bénéfice (au lieu de 5 ans avec process).',
  },
  {
    id: 'social',
    icon: '📱',
    accent: '#EC4899', // pink — visibility
    title: 'UN RESTO SANS PRÉSENCE SOCIALE UNIQUE',
    subtitle: 'ne fera pas exploser son CA.',
    quote: "« Les photos de plats, c'est l'enseigne 1995. Aujourd'hui le client veut voir TON visage avant d'entrer. »",
    anecdote: "Anaïs, restaurant signature à Monaco. 12 000 followers Instagram, contenus statiques, ticket moyen 180€ qui plafonnait depuis 18 mois. Engagement à 0.6%. Elle pensait qu'Instagram « ne marchait pas pour le premium ». On a tout retravaillé : SA voix, ses anecdotes de cuisine, ses choix d'approvisionnement, le chef en vidéos signature, les coulisses VIP. Résultat 60 jours plus tard : 1ère vidéo à 480K vues, +24 réservations premium par semaine venues d'Instagram, ticket moyen passé de 180€ à 270€.",
    lesson: 'Les restos premium qui scalent en 2026 sont ceux dont LE PATRON est connu. Votre visage > vos plats. Votre storytelling > vos photos. Votre authenticité > votre perfection.',
    cost: 'Sans stratégie sociale unique, vous loupes 30 à 50% de votre potentiel de CA. Vos concurrents qui jouent le jeu prennent votre part de marché chaque jour.',
  },
  {
    id: 'environnement',
    icon: '🎯',
    accent: '#22C55E', // green — holistic
    title: 'UN RESTO QUI GAGNE EN 2026',
    subtitle: 'maîtrise son environnement à 360°.',
    quote: "« Rien ne doit être laissé au hasard. La playlist, la déco, le polo, le votre du serveur, le pliage des serviettes — tout fait partie de la marque. »",
    anecdote: "Le pire mensonge qu'on raconte aux restaurateurs : « concentre-vous sur le produit, le reste suivra ». FAUX. J'ai audité un établissement à Genève qui faisait du 19/20 dans l'assiette et qui stagnait à 9% de marge nette. Pourquoi ? Service inégal, équipe sans codes, déco datée de 2014, page web pas mobile-friendly, aucune présence sur les apps premium (TheFork Premium, LFP, OpenTable Elite). On a réaligné les 14 points de contact en 6 semaines. CA +41%, marge nette passée à 19%.",
    lesson: 'Un client juge votre resto sur 30 micro-signaux avant même de goûter votre plat. Si UN seul est mal géré, votre produit doit être 2x meilleur pour compenser.',
    cost: 'L\'environnement, c\'est la différence entre un resto qui survit à 7-10% de marge et un resto premium qui scale à 18-25%. Différence = votre salaire qui double.',
  },
];

const ResultsStorytellingBlock = ({ restoName, firstName }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      ref={ref}
      className="my-6 rounded-3xl overflow-hidden relative"
      data-testid="results-storytelling-block"
      style={{
        background: 'linear-gradient(180deg, #0F0F12 0%, #1A0E0E 100%)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
        border: '1px solid rgba(240,90,26,0.20)',
      }}
    >
      {/* Top accent strip */}
      <div className="h-1" style={{ background: 'linear-gradient(90deg, #A78BFA, #F05A1A, #EC4899, #22C55E)' }} />

      {/* Intro */}
      <div className="px-5 sm:px-7 pt-6 pb-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex items-start gap-3 mb-4"
        >
          <SabrilPixarAvatar size={56} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black tracking-[3px] uppercase mb-1.5" style={{ color: '#F05A1A' }}>
              📜 La vérité que personne ne dit{firstName ? `, ${firstName}` : ''}
            </p>
            <p className="text-white font-bebas tracking-tight leading-[0.95]" style={{ fontSize: 'clamp(28px, 6.5vw, 42px)' }}>
              Pourquoi 80% des restos<br />
              <span style={{ color: '#F05A1A' }}>ne décollent jamais.</span>
            </p>
          </div>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-white/65 text-[13px] sm:text-sm leading-relaxed mb-2"
        >
          {restoName ? <>Pour <strong className="text-white">« {restoName} »</strong> aussi, ces 4 piliers vont décider de la suite.</> : 'Ces 4 piliers vont décider de la suite.'} J'ai accompagné <strong className="text-white">plus de 25 restaurants aux 4 coins du monde</strong> — USA, Asie, Maroc, Dubai et France — voici les 4 vérités qui séparent les restos premium qui scalent de ceux qui stagnent.
        </motion.p>
      </div>

      {/* Pillars */}
      <div className="px-5 sm:px-7 pb-6 space-y-4">
        {PILLARS.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, x: -16 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 + i * 0.15 }}
            className="rounded-2xl overflow-hidden relative"
            data-testid={`pillar-${p.id}`}
            style={{
              background: 'rgba(255,255,255,0.025)',
              border: `1px solid ${p.accent}30`,
              boxShadow: `0 8px 26px rgba(0,0,0,0.25), 0 0 18px ${p.accent}18`,
            }}
          >
            {/* Side accent bar */}
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: p.accent }} />

            <div className="pl-5 pr-4 py-4 sm:py-5 space-y-3">
              {/* Header */}
              <div className="flex items-start gap-3">
                <motion.span
                  className="text-3xl flex-shrink-0"
                  animate={{ scale: [1, 1.08, 1], rotate: [0, -3, 0] }}
                  transition={{ duration: 3.6, repeat: Infinity, delay: i * 0.4 }}
                >
                  {p.icon}
                </motion.span>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] font-black tracking-[2.5px] uppercase mb-1" style={{ color: p.accent }}>
                    Pilier {i + 1}/4
                  </p>
                  <h3 className="font-bebas leading-[0.95] tracking-tight text-white" style={{ fontSize: 'clamp(22px, 5vw, 28px)' }}>
                    {p.title}
                  </h3>
                  <p className="text-white/65 text-[13px] sm:text-sm font-medium leading-snug mt-0.5">
                    {p.subtitle}
                  </p>
                </div>
              </div>

              {/* Quote */}
              <blockquote
                className="border-l-2 pl-3 py-1 italic"
                style={{ borderColor: p.accent, color: '#FFFFFF' }}
              >
                <p className="text-[13px] sm:text-sm leading-relaxed font-medium" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {p.quote}
                </p>
                <p className="text-[10px] font-black tracking-[2px] uppercase mt-1.5" style={{ color: p.accent }}>
                  — Sabril
                </p>
              </blockquote>

              {/* Anecdote */}
              <div
                className="rounded-xl p-3"
                style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <p className="text-[9px] font-black tracking-[2px] uppercase mb-1.5 text-white/55">
                  📍 Histoire vraie
                </p>
                <p className="text-white/85 text-[12.5px] sm:text-[13px] leading-relaxed">
                  {p.anecdote}
                </p>
              </div>

              {/* Lesson */}
              <div
                className="rounded-xl p-3"
                style={{ background: `${p.accent}12`, border: `1px solid ${p.accent}30` }}
              >
                <p className="text-[9px] font-black tracking-[2px] uppercase mb-1" style={{ color: p.accent }}>
                  🎯 La leçon
                </p>
                <p className="text-white text-[12.5px] sm:text-[13px] leading-relaxed font-medium">
                  {p.lesson}
                </p>
              </div>

              {/* Cost */}
              <div
                className="rounded-xl p-3"
                style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)' }}
              >
                <p className="text-[9px] font-black tracking-[2px] uppercase mb-1 text-red-400">
                  💸 Ce que ça te coûte
                </p>
                <p className="text-white/90 text-[12.5px] sm:text-[13px] leading-relaxed">
                  {p.cost}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Closing punch */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 1.2 }}
        className="px-5 sm:px-7 py-6 text-center border-t border-white/8"
        style={{ background: 'rgba(240,90,26,0.06)' }}
      >
        <p className="text-white font-bebas tracking-tight leading-[1] mb-2" style={{ fontSize: 'clamp(22px, 5.5vw, 32px)' }}>
          Rien ne doit être laissé au hasard.
        </p>
        <p className="text-white/65 text-[13px] sm:text-sm italic leading-relaxed max-w-md mx-auto">
          {restoName ? <>« {restoName} »</> : 'Votre resto'} a besoin des 4 piliers alignés. Manquer ne serait-ce qu'un seul, c'est jouer à la roulette russe avec votre trésorerie.
        </p>
      </motion.div>
    </section>
  );
};

export default ResultsStorytellingBlock;
